import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as Collapsible from '@radix-ui/react-collapsible';
import { ChevronRight, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/utils/cn';
import {
  NAV_GROUPS,
  getActiveItemId,
  isPathActive,
  type NavItem,
} from '@/config/navigation';
import { SimpleTooltip } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Kbd } from '@/components/ui/kbd';
import { BrandLogo } from './BrandMark';

interface SidebarProps {
  /** Свёрнут до иконок (только на десктопе) */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  /** Вызывается при переходе — закрывает мобильное меню */
  onNavigate?: () => void;
}

const itemBase =
  'group/item relative flex h-8 w-full items-center gap-2.5 rounded-md px-2.5 text-[13px] font-medium outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/35';
const itemIdle = 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground';
const itemActive =
  'bg-surface text-foreground shadow-xs ring-1 ring-sidebar-border dark:bg-sidebar-accent dark:ring-white/[0.06]';

export const Sidebar: React.FC<SidebarProps> = ({ collapsed = false, onToggleCollapsed, onNavigate }) => {
  const location = useLocation();
  const { state } = useAuth();
  const role = state.user?.role;
  const activeId = getActiveItemId(location.pathname);

  const [openSections, setOpenSections] = useState<string[]>(() => (activeId ? [activeId] : []));

  // Раздел с активной страницей раскрывается автоматически
  useEffect(() => {
    if (activeId) {
      setOpenSections((prev) => (prev.includes(activeId) ? prev : [...prev, activeId]));
    }
  }, [activeId]);

  const canSee = (item: NavItem) => !item.roles || (role !== undefined && item.roles.includes(role));

  const renderItem = (item: NavItem) => {
    const Icon = item.icon;
    const isActive = activeId === item.id;
    const hasChildren = !!item.children?.length;

    // Свёрнутое меню: иконка с подсказкой, подразделы — во всплывающем списке
    if (collapsed) {
      if (hasChildren) {
        return (
          <DropdownMenu key={item.id}>
            <SimpleTooltip label={item.label} side="right">
              <DropdownMenuTrigger
                className={cn(itemBase, 'justify-center px-0', isActive ? itemActive : itemIdle)}
                aria-label={item.label}
              >
                <Icon className={cn('h-4 w-4', isActive && 'text-primary')} />
              </DropdownMenuTrigger>
            </SimpleTooltip>
            <DropdownMenuContent side="right" align="start" sideOffset={10} className="min-w-[13rem]">
              <DropdownMenuLabel>{item.label}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {item.children!.map((child) => (
                <DropdownMenuItem key={child.path} asChild>
                  <Link
                    to={child.path}
                    onClick={onNavigate}
                    className={cn(
                      isPathActive(child.path, location.pathname, location.search) && 'font-medium text-primary'
                    )}
                  >
                    {child.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      }
      return (
        <SimpleTooltip key={item.id} label={item.label} side="right">
          <Link
            to={item.path}
            onClick={onNavigate}
            aria-label={item.label}
            aria-current={isActive ? 'page' : undefined}
            className={cn(itemBase, 'justify-center px-0', isActive ? itemActive : itemIdle)}
          >
            <Icon className={cn('h-4 w-4', isActive && 'text-primary')} />
          </Link>
        </SimpleTooltip>
      );
    }

    if (hasChildren) {
      const isOpen = openSections.includes(item.id);
      return (
        <Collapsible.Root
          key={item.id}
          open={isOpen}
          onOpenChange={(open) =>
            setOpenSections((prev) => (open ? [...prev, item.id] : prev.filter((id) => id !== item.id)))
          }
        >
          <Collapsible.Trigger className={cn(itemBase, isActive && !isOpen ? itemActive : itemIdle, isActive && 'text-foreground')}>
            <Icon className={cn('h-4 w-4 shrink-0', isActive ? 'text-primary' : 'text-muted-foreground group-hover/item:text-foreground')} />
            <span className="flex-1 truncate text-left">{item.label}</span>
            <ChevronRight
              className={cn(
                'h-3.5 w-3.5 shrink-0 text-muted-foreground/70 transition-transform duration-200',
                isOpen && 'rotate-90'
              )}
            />
          </Collapsible.Trigger>
          <Collapsible.Content className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
            <div className="relative ml-[17px] mt-0.5 space-y-px border-l border-sidebar-border pb-1 pl-2.5">
              {item.children!.map((child) => {
                const childActive = isPathActive(child.path, location.pathname, location.search);
                return (
                  <Link
                    key={child.path}
                    to={child.path}
                    onClick={onNavigate}
                    aria-current={childActive ? 'page' : undefined}
                    className={cn(
                      'relative flex h-7 items-center rounded-md px-2.5 text-[13px] outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/35',
                      childActive
                        ? 'bg-sidebar-accent font-medium text-foreground before:absolute before:-left-[11px] before:top-1.5 before:h-4 before:w-0.5 before:rounded-full before:bg-primary'
                        : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                    )}
                  >
                    <span className="truncate">{child.label}</span>
                  </Link>
                );
              })}
            </div>
          </Collapsible.Content>
        </Collapsible.Root>
      );
    }

    return (
      <Link
        key={item.id}
        to={item.path}
        onClick={onNavigate}
        aria-current={isActive ? 'page' : undefined}
        className={cn(itemBase, isActive ? itemActive : itemIdle)}
      >
        <Icon className={cn('h-4 w-4 shrink-0', isActive ? 'text-primary' : 'text-muted-foreground group-hover/item:text-foreground')} />
        <span className="truncate">{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="flex h-full flex-col bg-sidebar">
      {/* Логотип */}
      <div className={cn('flex h-14 shrink-0 items-center', collapsed ? 'justify-center px-2' : 'px-4')}>
        <Link to="/dashboard" onClick={onNavigate} className="rounded-lg outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35" aria-label="DF-UPR — на главную">
          <BrandLogo collapsed={collapsed} />
        </Link>
      </div>

      {/* Разделы */}
      <nav
        aria-label="Основное меню"
        className={cn('scrollbar-none flex-1 overflow-y-auto pb-4 pt-1', collapsed ? 'px-2.5' : 'px-3')}
      >
        {NAV_GROUPS.map((group, index) => {
          const items = group.items.filter(canSee);
          if (!items.length) return null;
          return (
            <div key={group.id} className={cn(index > 0 && (collapsed ? 'mt-3 border-t border-sidebar-border pt-3' : 'mt-5'))}>
              {group.label && !collapsed && (
                <div className="mb-1 px-2.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground/75">
                  {group.label}
                </div>
              )}
              <div className="space-y-0.5">{items.map(renderItem)}</div>
            </div>
          );
        })}
      </nav>

      {/* Свернуть / развернуть (только десктоп) */}
      {onToggleCollapsed && (
        <div className={cn('shrink-0 border-t border-sidebar-border p-2.5', collapsed && 'flex justify-center')}>
          <SimpleTooltip
            label={
              <span className="flex items-center gap-2">
                {collapsed ? 'Развернуть меню' : 'Свернуть меню'}
                <Kbd className="border-background/20 bg-background/10 text-background/80">Ctrl B</Kbd>
              </span>
            }
            side="right"
          >
            <button
              type="button"
              onClick={onToggleCollapsed}
              className={cn(itemBase, itemIdle, collapsed ? 'w-8 justify-center px-0' : '')}
              aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
            >
              {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4 text-muted-foreground" />}
              {!collapsed && <span className="text-muted-foreground">Свернуть меню</span>}
            </button>
          </SimpleTooltip>
        </div>
      )}
    </div>
  );
};
